import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment-timezone";
import Button from "../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import toast from "react-hot-toast";
import Badge from "../../components/Badge.jsx";
import Pagination from "../../components/Pagination.jsx";
import {
    formatRuntimeDate,
    getProjectPhaseLabel,
    getRunModeLabel,
} from "../../components/runtime/runtimeStatusUtils.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";

const SchedulerSessionTab = {
    all: "all",
    running: "running",
    scheduled: "scheduled",
    completed: "completed",
};

const autoRefreshIntervalMs = 15000;

export default function ServiceStatus() {
    const {t} = useTranslation();
    const {context} = useContext(GlobalContext);
    const authorityConfiguration = useAuthorityConfiguration();

    const basePath = authorityConfiguration.basePath;
    const authorizationHeader = authorityConfiguration.headers?.authorization ?? null;

    const [selectedTab, setSelectedTab] = useState(SchedulerSessionTab.all);
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [page, setPage] = useState(1);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [response, setResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedSessionIds, setExpandedSessionIds] = useState([]);

    const requestIdRef = useRef(0);

    useEffect(() => {
        setPage(1);
        setExpandedSessionIds([]);
    }, [selectedProjectId, selectedTab]);

    const projectOptions = useMemo(() => {
        const availableProjects = [...(context.currentUserProjects ?? [])];
        availableProjects.sort((left, right) => left.name.localeCompare(right.name, "cs"));

        return [
            {
                value: "",
                label: t("runtime.scheduler.project_filter.all", {defaultValue: "Všechny projekty"}),
            },
            ...availableProjects.map((project) => ({
                value: project.id,
                label: project.name,
            })),
        ];
    }, [context.currentUserProjects, t]);

    const loadSessions = useCallback(async ({background = false} = {}) => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        if (background) {
            setIsRefreshing(true);
        }
        else {
            setIsLoading(true);
        }

        try {
            const queryString = new URLSearchParams();
            queryString.set("state", selectedTab);
            queryString.set("page", String(page));

            if (selectedProjectId !== "") {
                queryString.set("projectId", selectedProjectId);
            }

            const fetchResponse = await fetch(`${basePath}/api/v1/queue/sessions?${queryString.toString()}`, {
                headers: {
                    Accept: "application/json",
                    ...(authorizationHeader ? {authorization: authorizationHeader} : {}),
                },
            });

            const parsedBody = await fetchResponse.json().catch(() => null);

            if (!fetchResponse.ok) {
                const errorMessage = parsedBody?.message
                    ?? t("runtime.scheduler.fetch_error", {defaultValue: "Nepodařilo se načíst scheduler sessions."});

                throw new Error(errorMessage);
            }

            if (requestId !== requestIdRef.current) {
                return;
            }

            setResponse(parsedBody);
        } catch (error) {
            if (requestId === requestIdRef.current) {
                toast.error(
                    error instanceof Error
                        ? error.message
                        : t("runtime.scheduler.fetch_error", {defaultValue: "Nepodařilo se načíst scheduler sessions."}),
                );
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
                setIsRefreshing(false);
            }
        }
    }, [authorizationHeader, basePath, page, selectedProjectId, selectedTab, t]);

    useEffect(() => {
        void loadSessions();
    }, [loadSessions]);

    useEffect(() => {
        if (!autoRefreshEnabled) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            void loadSessions({background: true});
        }, autoRefreshIntervalMs);

        return () => {
            clearInterval(intervalId);
        };
    }, [autoRefreshEnabled, loadSessions]);

    const items = response?.data ?? [];
    const pagination = response?.pagination;
    const totalCount = pagination?.itemCount ?? items.length;

    const summaryBadges = useMemo(() => [
        {
            key: SchedulerSessionTab.all,
            color: "slate",
            label: t("runtime.scheduler.tabs.all", {defaultValue: "Vše"}),
        },
        {
            key: SchedulerSessionTab.running,
            color: "yellow",
            label: t("runtime.scheduler.tabs.running", {defaultValue: "Běží"}),
        },
        {
            key: SchedulerSessionTab.scheduled,
            color: "blue",
            label: t("runtime.scheduler.tabs.scheduled", {defaultValue: "Naplánováno"}),
        },
        {
            key: SchedulerSessionTab.completed,
            color: "emerald",
            label: t("runtime.scheduler.tabs.completed", {defaultValue: "Dokončeno"}),
        },
    ], [t]);

    function toggleExpanded(sessionId) {
        setExpandedSessionIds((currentExpandedSessionIds) => {
            if (currentExpandedSessionIds.includes(sessionId)) {
                return currentExpandedSessionIds.filter((currentSessionId) => currentSessionId !== sessionId);
            }

            return [...currentExpandedSessionIds, sessionId];
        });
    }

    return (
        <WideSecureLayout
            title={t("route.service-status")}
            actions={
                <div className={"flex flex-wrap items-center justify-center gap-3 md:justify-end"}>
                    <label className={"inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"}>
                        <input
                            type={"checkbox"}
                            className={"rounded border-slate-300 text-slate-900 focus:ring-slate-400"}
                            checked={autoRefreshEnabled}
                            onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
                        />
                        {t("runtime.scheduler.auto_refresh", {defaultValue: "Automaticky obnovovat"})}
                    </label>

                    <Button variant={"outline-primary"} onClick={() => void loadSessions({background: response !== null})}>
                        <FontAwesomeIcon icon={faRefresh}/>
                    </Button>
                </div>
            }
        >
            <div className={"space-y-6"}>
                <div className={"flex flex-wrap items-center gap-3"}>
                    {summaryBadges.map((summaryBadge) => (
                        <button
                            key={summaryBadge.key}
                            type={"button"}
                            onClick={() => setSelectedTab(summaryBadge.key)}
                            className={"rounded-full"}
                        >
                            <Badge
                                color={summaryBadge.color}
                                className={'px-2 py-1.5' + (selectedTab === summaryBadge.key ? ' ring-1 ring-slate-500' : '')}
                            >
                                {summaryBadge.label}
                            </Badge>
                        </button>
                    ))}

                    <div className={"text-sm text-slate-500 dark:text-slate-400"}>
                        <small>
                        {t("runtime.scheduler.total", {
                            defaultValue: "Celkem: {{count}}",
                            count: totalCount,
                        })}
                        </small>
                    </div>

                    {isRefreshing && (
                        <div className={"text-sm text-slate-500 dark:text-slate-400"}>
                            {t("runtime.scheduler.refreshing", {defaultValue: "Obnovuji..."})}
                        </div>
                    )}

                    <div className={"min-w-64 ml-auto"}>
                        <select
                            className={"form-select w-full"}
                            value={selectedProjectId}
                            onChange={(event) => setSelectedProjectId(event.target.value)}
                        >
                            {projectOptions.map((projectOption) => (
                                <option key={projectOption.value || "all-projects"} value={projectOption.value}>
                                    {projectOption.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <LoadingComponent loading={isLoading && response !== null} className={"rounded-2xl"}>
                    {response === null && isLoading ? (
                        <div className={"rounded-2xl border border-slate-200 bg-white p-8 dark:border-slate-700 dark:bg-slate-800"}>
                            <LoadingComponent loading={true} className={"min-h-48"} loadedClassName={"min-h-48"}/>
                        </div>
                    ) : (
                        <div className={"overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"}>
                            <div className={"overflow-x-auto"}>
                                <table className={"min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700"}>
                                    <thead className={"bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400"}>
                                    <tr>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.project", {defaultValue: "Projekt"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.state", {defaultValue: "Stav"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.planned_for", {defaultValue: "Naplánováno"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.started_at", {defaultValue: "Začalo"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.finished_at", {defaultValue: "Skončilo"})}</th>
                                        <th className={"px-4 py-3"}>{t("runtime.scheduler.error", {defaultValue: "Chyba"})}</th>
                                        <th className={"w-16 px-4 py-3"}>{t("runtime.scheduler.details", {defaultValue: "Detail"})}</th>
                                    </tr>
                                    </thead>
                                    <tbody className={"divide-y divide-slate-200 dark:divide-slate-700"}>
                                    {items.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className={"px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"}>
                                                {t("runtime.scheduler.empty", {defaultValue: "Pro vybraný filtr tu teď nic není."})}
                                            </td>
                                        </tr>
                                    )}

                                    {items.map((session) => {
                                        const isExpanded = expandedSessionIds.includes(session.id);
                                        const isExpandable = session.kind !== "historical_precalculation" && session.jobs.length > 0;

                                        return (
                                            <FragmentSessionRows
                                                key={session.id}
                                                session={session}
                                                isExpanded={isExpanded}
                                                isExpandable={isExpandable}
                                                onToggleExpanded={toggleExpanded}
                                                t={t}
                                            />
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </LoadingComponent>

                {pagination && pagination.pageCount > 1 && (
                    <Pagination
                        pagination={pagination}
                        onPageChange={(nextPage) => setPage(nextPage)}
                    />
                )}
            </div>
        </WideSecureLayout>
    );
}

function FragmentSessionRows({session, isExpanded, isExpandable, onToggleExpanded, t}) {
    return (
        <>
            <tr className={session.hasErrors ? "bg-rose-50/60 dark:bg-rose-950/10" : ""}>
                <td className={"px-4 py-4 align-top"}>
                    <div className={"font-medium text-black dark:text-slate-100"}>{session.projectName}</div>
                    <div className={"-mt-1 text-xs text-slate-500 dark:text-slate-400"}>
                        <small>{session.projectId}</small>
                    </div>
                    <div className={"mt-2 text-xs text-slate-500 dark:text-slate-400"}>
                        {getRunModeLabel(t, session.mode, session.kind)}
                    </div>
                </td>

                <td className={"px-4 py-4 align-top"}>
                    <SessionStatusBadge status={session.status} t={t}/>
                </td>

                <td className={"px-4 py-4 align-top"}>
                    <DateCell value={session.plannedFor}/>
                </td>

                <td className={"px-4 py-4 align-top"}>
                    <DateCell value={session.startedAt}/>
                </td>

                <td className={"px-4 py-4 align-top"}>
                    <DateCell value={session.finishedAt}/>
                </td>

                <td className={"px-4 py-4 align-top"}>
                    {session.hasErrors ? (
                        <div className={"space-y-2"}>
                            <Badge color={"rose"}>
                                {t("runtime.scheduler.error_badge", {defaultValue: "Chyba"})}
                            </Badge>
                            {session.errorMessage && (
                                <div className={"max-w-md whitespace-pre-wrap break-words text-xs text-rose-700 dark:text-rose-300"}>
                                    {session.errorMessage}
                                </div>
                            )}
                        </div>
                    ) : (
                        <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>
                    )}
                </td>

                <td className={"px-4 py-4 align-top"}>
                    {isExpandable && (
                        <Button
                            variant={"outline-black"}
                            size={"small"}
                            onClick={() => onToggleExpanded(session.id)}
                        >
                            {isExpanded
                                ? t("runtime.scheduler.collapse", {defaultValue: "Sbalit"})
                                : t("runtime.scheduler.expand", {defaultValue: "Detail"})}
                        </Button>
                    )}
                </td>
            </tr>

            {isExpanded && (
                <tr className={"bg-slate-50/80 dark:bg-slate-900/30"}>
                    <td colSpan={8} className={"px-4 py-4"}>
                        <SchedulerSessionJobsTable jobs={session.jobs} t={t}/>
                    </td>
                </tr>
            )}
        </>
    );
}

function SessionKindBadge({kind, t}) {
    if (kind === "historical_precalculation") {
        return (
            <Badge color={"blue"}>
                {t("runtime.scheduler.kind.historical_precalculation", {defaultValue: "Historický přepočet"})}
            </Badge>
        );
    }

    return (
        <Badge color={"slate"}>
            {t("runtime.scheduler.kind.standard", {defaultValue: "Basic session"})}
        </Badge>
    );
}

function SessionStatusBadge({status, t}) {
    if (status === "done") {
        return <Badge color={"emerald"}>{t("runtime.scheduler.status.done", {defaultValue: "Dokončeno"})}</Badge>;
    }

    if (status === "failed") {
        return <Badge color={"rose"}>{t("runtime.scheduler.status.failed", {defaultValue: "Fail"})}</Badge>;
    }

    if (status === "waiting" || status === "pending") {
        return <Badge color={"blue"}>{t("runtime.scheduler.status.scheduled", {defaultValue: "Naplánováno"})}</Badge>;
    }

    return (
        <Badge color={"yellow"}>
            {getProjectPhaseLabel(t, status) ?? t("runtime.scheduler.status.running", {defaultValue: "Běží"})}
        </Badge>
    );
}

function JobStatusBadge({status, t}) {
    switch (status) {
        case "done":
            return <Badge color={"emerald"}>{t("runtime.scheduler.job.done", {defaultValue: "Dokončeno"})}</Badge>;
        case "failed":
            return <Badge color={"rose"}>{t("runtime.scheduler.job.failed", {defaultValue: "Fail"})}</Badge>;
        case "processing":
            return <Badge color={"yellow"}>{t("runtime.scheduler.job.processing", {defaultValue: "Běží"})}</Badge>;
        case "skipped":
            return <Badge color={"slate"}>{t("runtime.scheduler.job.skipped", {defaultValue: "Přeskočeno"})}</Badge>;
        default:
            return <Badge color={"slate"}>{t("runtime.scheduler.job.pending", {defaultValue: "Čeká"})}</Badge>;
    }
}

function SchedulerSessionJobsTable({jobs, t}) {
    return (
        <div className={"overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"}>
            <div className={"overflow-x-auto"}>
                <table className={"min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700"}>
                    <thead className={"bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400"}>
                    <tr>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.integration", {defaultValue: "Integrace"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.state", {defaultValue: "Stav"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.started_at", {defaultValue: "Začalo"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.finished_at", {defaultValue: "Skončilo"})}</th>
                        <th className={"px-4 py-3"}>{t("runtime.scheduler.error", {defaultValue: "Chyba"})}</th>
                    </tr>
                    </thead>
                    <tbody className={"divide-y divide-slate-200 dark:divide-slate-700"}>
                    {jobs.map((job) => (
                        <tr key={job.integrationId}>
                            <td className={"px-4 py-3 align-top"}>
                                <div className={"font-medium text-slate-900 dark:text-slate-100"}>{job.integrationName}</div>
                                <div className={"mt-1 text-xs text-slate-500 dark:text-slate-400"}>{job.integrationId}</div>
                            </td>
                            <td className={"px-4 py-3 align-top"}>
                                <JobStatusBadge status={job.status} t={t}/>
                            </td>
                            <td className={"px-4 py-3 align-top"}>
                                <DateCell value={job.startedAt}/>
                            </td>
                            <td className={"px-4 py-3 align-top"}>
                                <DateCell value={job.finishedAt}/>
                            </td>
                            <td className={"px-4 py-3 align-top"}>
                                {job.failureMessage ? (
                                    <div className={"max-w-md whitespace-pre-wrap break-words text-xs text-rose-700 dark:text-rose-300"}>
                                        {job.failureMessage}
                                    </div>
                                ) : (
                                    <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function DateCell({value}) {
    const formattedValue = useMemo(() => {
        if (!value) {
            return null;
        }

        return formatRuntimeDate(value) ?? moment(value).format("DD.MM.YYYY HH:mm");
    }, [value]);

    if (!formattedValue) {
        return <span className={"text-xs text-slate-400 dark:text-slate-500"}>—</span>;
    }

    return <span className={"text-slate-700 dark:text-slate-200"}>{formattedValue}</span>;
}
